import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { runTask } from "@/lib/tasks";
import { Brain, CalendarDays, Video, LineChart, Sparkles, GitBranch, ArrowRight, Loader2, Film } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Dashboard() {
  const [brand, setBrand] = useState(null);
  const [plan, setPlan] = useState(null);
  const [today, setToday] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [review, setReview] = useState(null);
  const [gen, setGen] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const [b, p, t, m, r] = await Promise.all([
      api.get("/api/brand").then(r => r.data),
      api.get("/api/plans/weekly").then(r => r.data),
      api.get("/api/plans/today").then(r => r.data),
      api.get("/api/performance/list").then(r => r.data),
      api.get("/api/performance/review/latest").then(r => r.data),
    ]);
    setBrand(b); setPlan(p); setToday(t); setMetrics(m); setReview(r);
  };
  useEffect(() => { load(); }, []);

  const generatePlan = async () => {
    setGen(true);
    try {
      await runTask("/api/plans/generate", null, { intervalMs: 2500, maxMs: 240000 });
      toast.success("This week's plan is ready");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Plan generation failed");
    } finally { setGen(false); }
  };

  const dayName = new Date().toLocaleDateString("en-US", { weekday: "long" });

  return (
    <div className="min-h-screen">
      <div className="border-b border-border">
        <div className="px-8 py-8">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Command · {dayName}</div>
          <h1 className="mt-1 font-display font-black text-4xl tracking-tighter">Your marketing department, in one screen.</h1>
        </div>
      </div>

      <div className="p-8 grid grid-cols-12 gap-4">
        {/* Today */}
        <div className="col-span-12 lg:col-span-8 border border-border rounded p-6 bg-card" data-testid="card-today">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Today · Film this</div>
              <div className="font-display font-black text-2xl tracking-tight mt-1">{today?.post ? today.post.topic : "No post scheduled today"}</div>
            </div>
            {today?.post && (
              <Button data-testid="btn-goto-film" onClick={() => nav("/app/film")}>
                Open Film This <ArrowRight className="h-4 w-4"/>
              </Button>
            )}
          </div>
          {today?.post ? (
            <div className="mt-3">
              <div className="flex flex-wrap gap-2">
                <span className="pillar-badge">{today.post.goal}</span>
                <span className="pillar-badge">{today.post.platform}</span>
                <span className="pillar-badge">{today.post.format}</span>
                <span className="pillar-badge">{today.post.post_time}</span>
              </div>
              <div className="mt-5 font-display text-2xl font-bold leading-tight border-l-4 border-primary pl-4">
                “{today.post.hook}”
              </div>
              <div className="mt-4 text-muted-foreground text-sm leading-relaxed line-clamp-3">{today.post.script}</div>
            </div>
          ) : (
            <div className="text-muted-foreground text-sm">
              {plan ? "No post is scheduled for today in this week's plan." : "Generate this week's plan to see your daily Film This."}
              {!plan && (
                <div className="mt-4">
                  <Button data-testid="btn-generate-plan-1" onClick={generatePlan} disabled={gen}>
                    {gen ? <><Loader2 className="h-4 w-4 animate-spin"/> Building…</> : <><Sparkles className="h-4 w-4"/> Build this week's plan</>}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Brand */}
        <div className="col-span-12 lg:col-span-4 border border-border rounded p-6 bg-card" data-testid="card-brand">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground font-bold">
            <Brain className="h-3.5 w-3.5 text-primary"/> Brand Intelligence
          </div>
          <div className="mt-3 font-display font-bold text-xl tracking-tight leading-tight">
            {brand?.profile?.identity || "Complete onboarding to see your profile"}
          </div>
          <div className="mt-4">
            <Link to="/app/brand" data-testid="link-brand" className="text-sm font-semibold text-primary inline-flex items-center gap-1">
              Open profile <ArrowRight className="h-3.5 w-3.5"/>
            </Link>
          </div>
        </div>

        {/* Weekly plan */}
        <div className="col-span-12 lg:col-span-8 border border-border rounded p-6 bg-card" data-testid="card-plan">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground font-bold">
              <CalendarDays className="h-3.5 w-3.5 text-primary"/> This week's plan
            </div>
            {plan && <Button size="sm" variant="outline" data-testid="btn-regen-plan" onClick={generatePlan} disabled={gen}>
              {gen ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : "Regenerate"}
            </Button>}
          </div>
          {plan ? (
            <div className="mt-4 grid grid-cols-7 gap-2">
              {plan.posts.map((p) => (
                <div key={p.id} className="border border-border rounded p-3 text-xs bg-background">
                  <div className="font-bold text-foreground">{p.day.slice(0,3)}</div>
                  <div className={`mt-1 font-mono text-[10px] uppercase ${p.goal === "TOF" ? "text-primary" : p.goal === "MOF" ? "text-foreground" : "text-destructive"}`}>{p.goal}</div>
                  <div className="mt-2 text-muted-foreground line-clamp-3">{p.topic}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-3 text-sm text-muted-foreground">Generate a plan to see your 7-day layout.</div>
          )}
        </div>

        {/* Performance */}
        <div className="col-span-12 lg:col-span-4 border border-border rounded p-6 bg-card" data-testid="card-perf">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground font-bold">
            <LineChart className="h-3.5 w-3.5 text-primary"/> Performance
          </div>
          <div className="mt-3 font-display font-black text-4xl tracking-tight">{metrics.length}</div>
          <div className="text-xs text-muted-foreground">posts tracked</div>
          <Link to="/app/performance" data-testid="link-performance" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">
            Import metrics <ArrowRight className="h-3.5 w-3.5"/>
          </Link>
        </div>

        {/* Review */}
        <div className="col-span-12 lg:col-span-8 border border-border rounded p-6 bg-card" data-testid="card-review">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground font-bold">
            <Sparkles className="h-3.5 w-3.5 text-primary"/> AI Weekly Review
          </div>
          {review ? (
            <div className="mt-3">
              <div className="text-lg leading-relaxed">{review.summary}</div>
              <div className="mt-3 text-sm text-muted-foreground">
                Latest patterns: {review.patterns?.slice(0, 2).join(" · ")}
              </div>
              <Link to="/app/review" data-testid="link-review" className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                Open weekly review <ArrowRight className="h-3.5 w-3.5"/>
              </Link>
            </div>
          ) : (
            <div className="mt-3 text-sm text-muted-foreground">Import performance data and run your first review.</div>
          )}
        </div>

        {/* Belief */}
        <div className="col-span-12 lg:col-span-4 border border-border rounded p-6 bg-card" data-testid="card-belief">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground font-bold">
            <GitBranch className="h-3.5 w-3.5 text-primary"/> Belief Journeys
          </div>
          <div className="mt-3 text-sm">Design 7-step content sequences that move a specific belief from A to B.</div>
          <Link to="/app/belief" data-testid="link-belief" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">
            Design a journey <ArrowRight className="h-3.5 w-3.5"/>
          </Link>
        </div>
      </div>
    </div>
  );
}
